import React, { useEffect, useState, useMemo } from 'react';

const Hero: React.FC = () => {
  const [isVisible, setIsVisible] = useState(false);
  const [currentTitleIndex, setCurrentTitleIndex] = useState(0);
  const [currentText, setCurrentText] = useState('');
  const [isDeleting, setIsDeleting] = useState(false);

  const titles = useMemo(() => [
    'Tech Leader',
    'Full-Stack Developer',
    'A.I. Engineer',
    'UX/UI Perfectionist'
  ], []);

  useEffect(() => {
    setIsVisible(true);
  }, []);

  useEffect(() => {
    const currentTitle = titles[currentTitleIndex];
    const typingSpeed = isDeleting ? 25 : 45;
    const deletingSpeed = 25;
    const pauseTime = 1200;

    const typeText = () => {
      if (!isDeleting) {
        if (currentText.length < currentTitle.length) {
          setTimeout(() => {
            setCurrentText(currentTitle.slice(0, currentText.length + 1));
          }, typingSpeed);
        } else {
          setTimeout(() => {
            setIsDeleting(true);
          }, pauseTime);
        }
      } else {
        if (currentText.length > 0) {
          setTimeout(() => {
            setCurrentText(currentTitle.slice(0, currentText.length - 1));
          }, deletingSpeed);
        } else {
          setIsDeleting(false);
          setCurrentTitleIndex((prevIndex) => (prevIndex + 1) % titles.length);
        }
      }
    };

    const timer = setTimeout(typeText, isDeleting ? deletingSpeed : typingSpeed);
    return () => clearTimeout(timer);
  }, [currentText, isDeleting, currentTitleIndex, titles]);

  const scrollToProjects = () => {
    const element = document.querySelector('#projects');
    if (element) {
      element.scrollIntoView({ behavior: 'smooth' });
    }
  };

  return (
    <section id="home" className="min-h-[800px] flex items-center justify-center relative overflow-hidden">
      {/* Background Elements */}
      <div className="absolute inset-0 overflow-hidden">
        <div className="absolute top-20 left-10 w-72 h-72 bg-primary-800 rounded-full mix-blend-screen filter blur-xl opacity-70 animate-float"></div>
        <div className="absolute top-40 right-10 w-72 h-72 bg-blue-800 rounded-full mix-blend-screen filter blur-xl opacity-70 animate-float" style={{ animationDelay: '2s' }}></div>
        <div className="absolute -bottom-8 left-20 w-72 h-72 bg-purple-800 rounded-full mix-blend-screen filter blur-xl opacity-70 animate-float" style={{ animationDelay: '4s' }}></div>
      </div>

      <div className="container-custom relative z-10">
        <div className="text-center max-w-4xl mx-auto px-4 sm:px-6 lg:px-8">
          {/* Profile Image */}
          <div className={`mb-8 transition-all duration-1000 ${isVisible ? 'opacity-100 translate-y-0' : 'opacity-0 translate-y-10'}`}>
            <div className="relative inline-block">
              <img
                src="/assets/png/richard-baird.png"
                alt="Richard J Baird"
              className="w-32 h-32 md:w-40 md:h-40 rounded-full border-4 border-dark-700 shadow-2xl mx-auto"
              />
              <div className="absolute -bottom-2 -right-2 w-8 h-8 bg-green-500 rounded-full border-4 border-dark-700 animate-pulse"></div>
            </div>
          </div>

          {/* Main Heading */}
          <h1 className={`text-4xl md:text-6xl lg:text-7xl font-bold mb-6 transition-all duration-1000 delay-300 ${
            isVisible ? 'opacity-100 translate-y-0' : 'opacity-0 translate-y-10'
          }`}>
            <span className="text-white">I'm Richard</span>
            <br />
            <span className="gradient-text">
              {currentText}
              <span className="inline-block w-0.5 h-full bg-current animate-pulse" style={{ animationDuration: '1s' }}></span>
            </span>
          </h1>

          {/* Subtitle */}
          <p className={`text-xl md:text-2xl text-dark-300 mb-8 transition-all duration-1000 delay-500 ${
            isVisible ? 'opacity-100 translate-y-0' : 'opacity-0 translate-y-10'
          }`}>
            Driven by Curiosity, <br className="hidden sm:block" />
            <span className="text-primary-400 font-semibold">
              Building The Future of Technology
            </span>
          </p>

          {/* CTA Buttons */}
          <div className={`flex flex-col sm:flex-row gap-4 justify-center items-center transition-all duration-1000 delay-700 ${
            isVisible ? 'opacity-100 translate-y-0' : 'opacity-0 translate-y-10'
          }`}>
            <button
              onClick={scrollToProjects}
              className="btn-primary group flex"
            >
              See What I've Built
              <svg className="rotate-90 w-5 h-5 ml-2 group-hover:translate-x-1 transition-transform duration-200" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M13 7l5 5m0 0l-5 5m5-5H6" />
              </svg>
            </button>
            <button
              onClick={() => document.querySelector('#contact')?.scrollIntoView({ behavior: 'smooth' })}
              className="btn-secondary"
            >
              Get In Touch
            </button>
          </div>

          {/* Social Links */}
          <div className={`flex justify-center space-x-6 mt-12 transition-all duration-1000 delay-1000 ${
            isVisible ? 'opacity-100 translate-y-0' : 'opacity-0 translate-y-10'
          }`}>
            <a
              href="https://linkedin.com/in/richardjbaird"
              target="_blank"
              rel="noopener noreferrer"
              className="p-3 bg-dark-800 rounded-full shadow-lg hover:shadow-xl transition-all duration-300 transform hover:scale-110 hover:-translate-y-1"
            >
              <img
                src="/assets/png/linkedin-ico.png"
                alt="LinkedIn"
                className="w-6 h-6"
              />
            </a>
            <a
              href="https://github.com/rjcbaird"
              target="_blank"
              rel="noopener noreferrer"
              className="p-3 bg-dark-800 rounded-full shadow-lg hover:shadow-xl transition-all duration-300 transform hover:scale-110 hover:-translate-y-1"
            >
              <img
                src="/assets/png/github-ico.png"
                alt="GitHub"
                className="w-6 h-6"
              />
            </a>
          </div>

          {/* Scroll Indicator */}
          {/* <div className={`absolute bottom-8 left-1/2 transform -translate-x-1/2 transition-all duration-1000 delay-1200 ${
            isVisible ? 'opacity-100 translate-y-0' : 'opacity-0 translate-y-10'
          }`}>
            <div className="flex flex-col items-center space-y-2">
              <span className="text-sm text-dark-500 dark:text-dark-400">Scroll Down</span>
              <div className="w-6 h-10 border-2 border-dark-300 dark:border-dark-600 rounded-full flex justify-center">
                <div className="w-1 h-3 bg-dark-300 dark:bg-dark-600 rounded-full mt-2 animate-bounce"></div>
              </div>
            </div>
          </div> */}
        </div>
      </div>
    </section>
  );
};

export default Hero;
